import { useCallback, useEffect, useState } from "react";
import { Sparkles, Plus, CheckCircle2, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function EvaAdmin() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [newPrompt, setNewPrompt] = useState("");
  const [saving, setSaving] = useState(false);
  const [modelEdit, setModelEdit] = useState("");
  const [limitsEdit, setLimitsEdit] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/eva/config");
      setConfig(data);
      setModelEdit(data.provider?.model || "");
      setLimitsEdit({
        max_requests_per_user_day: data.limits?.max_requests_per_user_day,
        max_tool_calls_per_run: data.limits?.max_tool_calls_per_run,
        max_tokens_per_run: data.limits?.max_tokens_per_run,
      });
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleTool = async (tool, active) => {
    try {
      await api.put(`/admin/eva/tools/${tool.tool_code}`, { active });
      toast.success(`Tool ${active ? "abilitato" : "disabilitato"}`, { description: tool.tool_code });
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const activatePrompt = async (version) => {
    try {
      await api.post(`/admin/eva/prompts/${version}/activate`);
      toast.success(`Prompt v${version} attivato`);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const createPrompt = async () => {
    setSaving(true);
    try {
      await api.post("/admin/eva/prompts", { prompt_text: newPrompt, activate: false });
      toast.success("Nuova versione del prompt creata (non attiva)");
      setNewPrompt("");
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const saveModel = async () => {
    try {
      await api.put("/admin/eva/provider", { model: modelEdit });
      toast.success("Modello aggiornato", { description: modelEdit });
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const toggleFlag = async (flag, enabled) => {
    try {
      await api.put(`/admin/eva/flags/${flag.code}`, { enabled });
      toast.success(`Flag ${flag.code} ${enabled ? "attivato" : "disattivato"}`);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const saveLimits = async () => {
    try {
      const payload = Object.fromEntries(
        Object.entries(limitsEdit).map(([k, v]) => [k, Number(v)]));
      await api.put("/admin/eva/limits", payload);
      toast.success("Limiti di utilizzo aggiornati");
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="eva-admin-error">{error}</p>;
  if (!config) return <p className="text-sm text-slate-600" data-testid="eva-admin-loading">Caricamento EVA Admin…</p>;

  const { provider, prompt_versions, tools, usage, feedback, limits, runs, feature_flags } = config;

  return (
    <div className="space-y-6" data-testid="eva-admin-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-green-800" aria-hidden="true" />
          EVA Admin
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Configurazione dell'assistente Eva: provider, prompt versionato, tool registry, utilizzo e feedback.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200" data-testid="eva-provider-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Provider AI</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Provider</span>
              <strong data-testid="eva-provider-name">{provider?.provider}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Stato</span>
              <Badge variant="outline" className={provider?.enabled
                ? "bg-green-50 text-green-800 border-green-300" : "bg-red-50 text-red-800 border-red-300"}
                data-testid="eva-provider-status">
                {provider?.enabled ? "ATTIVO" : "DISABILITATO"}</Badge></div>
            <div className="flex justify-between items-center gap-2">
              <span className="text-slate-600">Modello</span>
              <div className="flex items-center gap-1.5">
                <Input className="h-8 w-36 text-xs" value={modelEdit}
                  onChange={(e) => setModelEdit(e.target.value)} data-testid="eva-model-input" />
                <Button size="sm" variant="outline" className="h-8" onClick={saveModel}
                  data-testid="eva-model-save">Salva</Button>
              </div>
            </div>
            <div className="flex justify-between"><span className="text-slate-600">Max output token</span>
              <strong>{provider?.max_output_tokens}</strong></div>
            <p className="text-[10px] text-slate-400">La chiave API è conservata lato server e non viene mai mostrata.</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="eva-usage-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Utilizzo (AI Usage Log)</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Richieste totali</span>
              <strong data-testid="eva-usage-requests">{usage.total_requests}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Token input</span>
              <strong>{usage.total_input_tokens}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Token output</span>
              <strong>{usage.total_output_tokens}</strong></div>
            <p className="text-[10px] text-slate-400">
              Limite richieste: {limits?.max_requests_per_user_day}/utente/giorno ·{" "}
              {limits?.max_tool_calls_per_run} tool call per risposta
            </p>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="eva-feedback-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Feedback utenti</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Positivi</span>
              <strong className="text-green-800" data-testid="eva-feedback-positive">{feedback.positive}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Negativi</span>
              <strong className="text-red-700" data-testid="eva-feedback-negative">{feedback.negative}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Totali</span>
              <strong>{feedback.total}</strong></div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200" data-testid="eva-runs-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Run e conferme (Fase 14)</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Run totali</span>
              <strong data-testid="eva-runs-total">{runs?.total ?? 0}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Tool call (recenti)</span>
              <strong>{runs?.total_tool_calls ?? 0}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Conferme in attesa</span>
              <strong className="text-amber-700" data-testid="eva-confirmations-pending">
                {runs?.confirmations?.PENDING ?? 0}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Conferme eseguite</span>
              <strong className="text-green-800">{runs?.confirmations?.EXECUTED ?? 0}</strong></div>
            <div className="flex justify-between"><span className="text-slate-600">Annullate/scadute</span>
              <strong>{(runs?.confirmations?.CANCELLED ?? 0) + (runs?.confirmations?.EXPIRED ?? 0)}</strong></div>
            {(runs?.recent || []).length > 0 && (
              <div className="pt-2 border-t border-slate-100 max-h-40 overflow-y-auto">
                <p className="text-[10px] font-semibold text-slate-500 mb-1">Ultime run</p>
                <ul className="space-y-1">
                  {runs.recent.slice(0, 8).map((r) => (
                    <li key={r.id} className="text-[10px] text-slate-600 flex justify-between gap-2">
                      <span className="truncate">{r.model} · {r.tool_calls_count} tool</span>
                      <span>{(r.started_at || "").slice(0, 16).replace("T", " ")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="eva-flags-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Feature flags per modulo</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(feature_flags || []).map((f) => (
              <div key={f.code} className="flex items-center justify-between gap-2"
                data-testid={`eva-flag-${f.code}`}>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-slate-800 truncate">{f.code}</p>
                  <p className="text-[10px] text-slate-500 truncate">{f.description}</p>
                </div>
                <Switch checked={!!f.enabled}
                  onCheckedChange={(v) => toggleFlag(f, v)}
                  data-testid={`eva-flag-toggle-${f.code}`}
                  aria-label={`Attiva flag ${f.code}`} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="eva-limits-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Limiti di utilizzo</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="space-y-1">
              <Label htmlFor="eva-limit-day" className="text-xs">Richieste per utente/giorno</Label>
              <Input id="eva-limit-day" type="number" min="1" className="h-8 text-xs"
                value={limitsEdit.max_requests_per_user_day ?? ""}
                onChange={(e) => setLimitsEdit((l) => ({ ...l, max_requests_per_user_day: e.target.value }))}
                data-testid="eva-limit-day-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="eva-limit-tools" className="text-xs">Tool call per risposta</Label>
              <Input id="eva-limit-tools" type="number" min="1" className="h-8 text-xs"
                value={limitsEdit.max_tool_calls_per_run ?? ""}
                onChange={(e) => setLimitsEdit((l) => ({ ...l, max_tool_calls_per_run: e.target.value }))}
                data-testid="eva-limit-tools-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="eva-limit-tokens" className="text-xs">Token per risposta</Label>
              <Input id="eva-limit-tokens" type="number" min="1" className="h-8 text-xs"
                value={limitsEdit.max_tokens_per_run ?? ""}
                onChange={(e) => setLimitsEdit((l) => ({ ...l, max_tokens_per_run: e.target.value }))}
                data-testid="eva-limit-tokens-input" />
            </div>
            <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={saveLimits}
              data-testid="eva-limits-save">
              <ShieldCheck className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Salva limiti
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200" data-testid="eva-prompts-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">System Prompt — versioni</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {prompt_versions.map((p) => (
            <div key={p.id} className="rounded-lg border border-slate-200 p-4"
              data-testid={`eva-prompt-v${p.version}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Versione {p.version}</span>
                  {p.active && (
                    <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300">ATTIVA</Badge>)}
                </div>
                {!p.active && (
                  <Button size="sm" variant="outline" onClick={() => activatePrompt(p.version)}
                    data-testid={`eva-prompt-activate-v${p.version}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Attiva
                  </Button>
                )}
              </div>
              <pre className="text-xs text-slate-600 whitespace-pre-wrap max-h-32 overflow-y-auto bg-slate-50 rounded p-2">{p.prompt_text}</pre>
            </div>
          ))}
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <Label htmlFor="eva-new-prompt">Nuova versione del prompt</Label>
            <Textarea id="eva-new-prompt" rows={4} value={newPrompt}
              onChange={(e) => setNewPrompt(e.target.value)}
              placeholder="Scrivi la nuova versione del System Prompt…"
              data-testid="eva-new-prompt" />
            <Button size="sm" className="bg-green-700 hover:bg-green-800" disabled={saving || newPrompt.trim().length < 50}
              onClick={createPrompt} data-testid="eva-prompt-create">
              <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Crea versione (bozza)
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="eva-tools-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Tool Registry ({tools.length} tool)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="eva-tools-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3 font-semibold">Tool</th>
                  <th className="py-2 pr-3 font-semibold">Modulo</th>
                  <th className="py-2 pr-3 font-semibold">Permesso</th>
                  <th className="py-2 pr-3 font-semibold">Tipo</th>
                  <th className="py-2 font-semibold">Attivo</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((t) => (
                  <tr key={t.tool_code} className="border-b border-slate-100 last:border-0"
                    data-testid={`eva-tool-${t.tool_code}`}>
                    <td className="py-2 pr-3">
                      <p className="font-mono text-xs text-slate-800">{t.tool_code}</p>
                      <p className="text-[10px] text-slate-500 max-w-[280px] truncate">{t.description}</p>
                    </td>
                    <td className="py-2 pr-3 text-xs">{t.module}</td>
                    <td className="py-2 pr-3 font-mono text-[10px] text-slate-600">{t.permission_required}</td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-col gap-1 items-start">
                        <Badge variant="outline" className={t.read_only
                          ? "bg-sky-50 text-sky-800 border-sky-300 text-[10px]"
                          : "bg-amber-50 text-amber-800 border-amber-300 text-[10px]"}>
                          {t.read_only ? "READ-ONLY" : "WRITE"}
                        </Badge>
                        {t.side_effect_level && t.side_effect_level !== "NONE" && (
                          <Badge variant="outline"
                            className={`text-[10px] ${t.side_effect_level === "HIGH"
                              ? "bg-red-50 text-red-800 border-red-300"
                              : "bg-amber-50 text-amber-800 border-amber-300"}`}
                            data-testid={`eva-tool-risk-${t.tool_code}`}>
                            {t.side_effect_level}{t.confirmation_required ? " + CONFERMA" : ""}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-2">
                      <Switch checked={t.active} onCheckedChange={(v) => toggleTool(t, v)}
                        data-testid={`eva-tool-toggle-${t.tool_code}`}
                        aria-label={`Attiva tool ${t.tool_code}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
